/**
 * Dashboard — spec §5-F13, §8.1 `/dashboard`.
 *
 * Everyone gets the personal dashboard. Owners and admins additionally get the
 * executive one, because `report.viewWorkspace` is theirs alone — so the page
 * adapts rather than 403-ing for two thirds of the company.
 */

import { PERMISSIONS, SEMANTIC_SUCCESS } from '@virtuetasks/types';
import { AlertTriangle, Ban, Download, RefreshCcw } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCan } from '@/features/auth';
import {
  ChartCard,
  PRIORITY_COLOURS,
  SimpleBarChart,
  SimpleLineChart,
  StatCard,
} from './charts';
import {
  reportExportUrl,
  useExecutiveDashboard,
  usePersonalDashboard,
} from './api';

function PersonalSection() {
  const { data, isLoading } = usePersonalDashboard();

  if (isLoading || !data) return <Skeleton className="h-28" />;

  const byPriority = (['P0', 'P1', 'P2', 'P3'] as const).map((p) => ({
    priority: p,
    count: data.openByPriority[p] ?? 0,
  }));
  const capacityPct = data.capacityHoursThisWeek
    ? Math.round((data.hoursLoggedThisWeek / data.capacityHoursThisWeek) * 100)
    : 0;

  return (
    <section className="flex flex-col gap-3" data-testid="personal-dashboard">
      <h2 className="text-[13px] font-medium">My week</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Overdue"
          value={data.overdue}
          tone={data.overdue > 0 ? 'danger' : 'default'}
          testId="stat-overdue"
        />
        <StatCard label="Completed (7d)" value={data.completedLast7Days} />
        <StatCard
          label="Logged this week"
          value={`${data.hoursLoggedThisWeek}h`}
          hint={`of ${data.capacityHoursThisWeek}h capacity · ${capacityPct}%`}
          tone={capacityPct > 100 ? 'warn' : 'default'}
        />
        <StatCard label="Review queue" value={data.reviewQueue} />
      </div>

      <ChartCard title="My open work by priority" testId="chart-priority">
        <SimpleBarChart
          data={byPriority}
          xKey="priority"
          yKey="count"
          height={180}
          cellColour={(row) => PRIORITY_COLOURS[String(row.priority)] ?? '#7C838A'}
        />
      </ChartCard>
    </section>
  );
}

function ExecutiveSection() {
  const { data, isLoading, refetch, isFetching } = useExecutiveDashboard();

  if (isLoading || !data) return <Skeleton className="h-64" />;

  // Throughput arrives per week per project; stack it into one row per week.
  const weeks = [...new Set(data.throughput.map((t) => t.week))].sort();
  const projectKeys = [...new Set(data.throughput.map((t) => t.projectKey))];
  const throughputByWeek = weeks.map((week) => {
    const row: Record<string, unknown> = { week: week.slice(5) };
    for (const key of projectKeys) {
      row[key] = data.throughput.find((t) => t.week === week && t.projectKey === key)?.completed ?? 0;
    }
    return row;
  });

  const palette = [SEMANTIC_SUCCESS, '#4A7CB5', '#C9762B', '#7C838A', '#C2413A', '#6B7076'];

  return (
    <section className="flex flex-col gap-3" data-testid="executive-dashboard">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-[13px] font-medium">Workspace</h2>
        <Button variant="ghost" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCcw className={isFetching ? 'animate-spin' : undefined} />
          Refresh
        </Button>
        <a href={reportExportUrl('throughput')} download>
          <Button variant="ghost" size="sm">
            <Download />
            Export CSV
          </Button>
        </a>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Cycle time (median)"
          value={`${data.cycleTime.medianDays}d`}
          hint={`p85 ${data.cycleTime.p85Days}d · n=${data.cycleTime.sample}`}
          testId="stat-cycle-time"
        />
        <StatCard
          label="Lead time (median)"
          value={`${data.leadTime.medianDays}d`}
          hint={`p85 ${data.leadTime.p85Days}d`}
        />
        <StatCard
          label="Blocked"
          value={data.blocked.count}
          hint={
            data.blocked.longest
              ? `longest ${data.blocked.longest.key} · ${data.blocked.longest.days}d`
              : 'nothing blocked'
          }
          tone={data.blocked.count > 0 ? 'warn' : 'default'}
        />
        <StatCard
          label="Reopen rate"
          value={`${Math.round(data.reopenRate * 100)}%`}
          hint="of completed work"
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <ChartCard
          title="Throughput"
          subtitle="Tasks completed per week, by project"
          testId="chart-throughput"
        >
          {throughputByWeek.length === 0 ? (
            <p className="py-8 text-center text-[12px] vt-muted">
              Nothing completed in the last 12 weeks.
            </p>
          ) : (
            <SimpleLineChart
              data={throughputByWeek}
              xKey="week"
              series={projectKeys.map((k, i) => ({
                key: k,
                label: k,
                colour: palette[i % palette.length],
              }))}
            />
          )}
        </ChartCard>

        <ChartCard title="Overdue by project" testId="chart-overdue">
          {data.overdueByProject.length === 0 ? (
            <p className="py-8 text-center text-[12px] vt-muted">Nothing overdue. Rare and good.</p>
          ) : (
            <SimpleBarChart
              data={data.overdueByProject as unknown as Record<string, unknown>[]}
              xKey="projectKey"
              yKey="count"
              colour="#C2413A"
            />
          )}
        </ChartCard>

        <ChartCard
          title="Capacity utilisation"
          subtitle="This week, leave-adjusted"
          testId="chart-capacity"
        >
          {data.capacity.length === 0 ? (
            <p className="py-8 text-center text-[12px] vt-muted">No capacity data.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {data.capacity.map((person) => {
                const pct = Math.round(person.utilisation * 100);
                // Quota health: red over, amber near, green fine — meaning, not brand.
                const colour = pct > 100 ? '#C2413A' : pct > 80 ? '#C9762B' : SEMANTIC_SUCCESS;
                return (
                  <li key={person.userId} className="flex items-center gap-2">
                    <span className="w-28 shrink-0 truncate text-[12px]">{person.name}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--surface-2)]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(pct, 150)}%`, background: colour }}
                      />
                    </div>
                    <span className="vt-mono w-10 shrink-0 text-right text-[11px]">{pct}%</span>
                  </li>
                );
              })}
            </ul>
          )}
        </ChartCard>

        <ChartCard title="WIP by person" subtitle="Flagged above 5 in progress" testId="chart-wip">
          {data.wipByPerson.length === 0 ? (
            <p className="py-8 text-center text-[12px] vt-muted">Nothing in progress.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {data.wipByPerson.map((person) => (
                <li key={person.userId} className="flex items-center gap-2 text-[12px]">
                  <span className="flex-1 truncate">{person.name}</span>
                  {person.flagged && (
                    <Badge tone="warn">
                      <AlertTriangle className="size-3" />
                      overloaded
                    </Badge>
                  )}
                  <span className="vt-mono">{person.wip}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>

      {data.onLeaveToday.length > 0 && (
        <div className="vt-surface flex items-center gap-2 p-3 text-[13px]">
          <Ban className="size-4 text-[var(--text-muted)]" />
          On leave today: {data.onLeaveToday.join(', ')}
        </div>
      )}
    </section>
  );
}

export function DashboardPage() {
  const can = useCan();
  const isExec = can(PERMISSIONS.REPORT_VIEW_WORKSPACE);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={isExec ? 'Your week, and the whole workspace.' : 'Your week at a glance.'}
      />
      <div className="flex flex-col gap-6 p-4">
        <PersonalSection />
        {isExec && <ExecutiveSection />}
      </div>
    </>
  );
}
